import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { ScrollView, Text, View } from "react-native"
import { textStyles } from "../style"
import { ThemeContext } from "../utility/context/ThemeContext"
import { usePlaidLink } from "../utility/context"
import { useAppSelector } from "../store"
import { useIsFocused } from "@react-navigation/native"
import { Ionicons } from "@expo/vector-icons"
import { ThemedButton } from "../utility/input/buttons"
import { AccountCard } from "../account-card"
import { CustomActionSheet, CustomActionSheetRef } from "../utility/CustomActionSheet"
import { DashboardAccountInfo } from "@ecountabl/lib"
import AccountManagement from "../utility/forms/account-management"
import { MAX_CONTENT_WIDTH } from "../config"
import Loader from "../utility/loader"
import Typewriter from "../utility/typewriter"

type Props = {
  setStepHandler: (handler: () => Promise<boolean> | void) => void
  setNextEnabled: (enabled: boolean) => void
}

const AddAccounts = ({ setStepHandler, setNextEnabled }: Props) => {
  const theme = useContext(ThemeContext).theme
  const { textMain, primary, white } = theme.colors

  const { accounts, status: accountsStatus } = useAppSelector((state) => state.accountsSlice)
  const accountsLoading = accountsStatus === 'idle' || accountsStatus === 'loading'

  const { startPlaidLinkSession } = usePlaidLink()
  const isFocused = useIsFocused()

  useEffect(() => {
    if (!isFocused) return

    setNextEnabled(true)

    setStepHandler(undefined)
  }, [accounts,isFocused])

  const manageAccountsSheetRef = useRef<CustomActionSheetRef>(null)

  const [accountToManage, setAccountToManage] = useState<DashboardAccountInfo>(null)

  const textContent = useMemo(() => {
    return "Great! Now let's connect your accounts so we can analyze your spending and provide insights."
  }, [])

  const addAccountsButton = useMemo(() => {
    return (
      <ThemedButton
        text="Add Accounts"
        onPress={() => {
          startPlaidLinkSession({
            redirectPath: "onboarding/add-accounts",
          })
        }}
        variant="icon"
        Icon={() => <Ionicons name="add-circle" color={white} style={{fontSize: textStyles.body.fontSize}} />}
        style={{
          backgroundColor: primary,
        }}
      />
    )
  }, [])

  return <>
    <View style={{gap: 16, alignSelf: "center", width: "100%", maxWidth: MAX_CONTENT_WIDTH, marginBottom: 16}}>
      <Text style={[textStyles.body, {color: textMain}]}>
        {textContent}
      </Text>
      {addAccountsButton}
    </View>

    <ScrollView
      style={{flex: 1, width: "100%", maxWidth: MAX_CONTENT_WIDTH}}
      contentContainerStyle={
        !accountsLoading && accounts?.length > 0 ? {
          gap: 8,
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "space-evenly",
          width: "100%",
        }
        : {
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          width: "100%",
        }
      }
    >
      {
        accountsLoading ? (
          <View style={{justifyContent: "center", alignItems: "center", flex: 1}}>
            <Loader size="large" />
            <Text style={[textStyles.body, {color: textMain}]}>
              Fetching your accounts <Typewriter text="..." style={textStyles.body} restartDelay={500}/>
            </Text>
          </View>
        ) : (
        accounts?.length > 0 ? (
          accounts?.map((account) => (
            <AccountCard
              key={account.account_id}
              account={account}
              onMenuPress={() => {
                setAccountToManage(account)
                manageAccountsSheetRef.current?.show()
              }}
            />
          ))
        ) : (
          <View style={{justifyContent: "center", alignItems: "center"}}>
            <Ionicons name="wallet-outline" size={72} color={textMain} style={{ marginBottom: 12, opacity: 0.8 }} />
            <Text style={[textStyles.h4, { color: textMain, textAlign: "center", marginBottom: 6 }]}>
              No accounts linked yet
            </Text>
            <Text style={[textStyles.body, { color: textMain, textAlign: "center" }]}>
              You don&apos;t have any accounts linked. Press{" "}
              <Text style={{fontWeight: "bold"}}>Add Accounts</Text>{" "}
              to link your accounts.
            </Text>
          </View>
        ))
      }
    </ScrollView>
    {/* manage accounts sheet */}
    <CustomActionSheet
      ref={manageAccountsSheetRef}
      name='onboarding-add-accounts'
    >
      <AccountManagement
        account={accountToManage}
        closeSheet={() => {
          manageAccountsSheetRef.current?.hide()
        }}
      />
    </CustomActionSheet>
  </>
}

export default AddAccounts